import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { validationSchemas } from '@client/util/policyApplicationFieldValidation';

export const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
] as const;

export const MARITAL_STATUS_OPTIONS = [
  { value: 'Never married', label: 'Never married' },
  { value: 'Married', label: 'Married' },
  { value: 'Partnered', label: 'Partnered' },
  { value: 'Separated', label: 'Separated' },
  { value: 'Divorced', label: 'Divorced' },
  { value: 'Widowed', label: 'Widowed' },
] as const;

export const quickPlanSchema = z.object({
  firstName: z.string().trim().min(3, 'First name must be at least 3 characters'),
  lastName: z.string().trim().min(2, 'Last name must be at least 2 characters'),
  age: validationSchemas.age,
  sex: z.enum(['male', 'female'], {
    required_error: 'Please select a sex',
  }),
  smokingStatus: z.enum(['never', 'former', 'current'], {
    required_error: 'Please select smoking status',
  }),
  zipCode: z
    .string()
    .min(1, 'Zip code is required')
    .regex(/^\d{5}$/, 'Must be a 5-digit zip code'),
  maritalStatus: z.string().min(1, 'Marital status is required'),
});

export type QuickPlanFormValues = z.infer<typeof quickPlanSchema>;

interface UseQuickPlanFormOptions {
  onSubmit: (data: QuickPlanFormValues) => void;
}

export function useQuickPlanForm({ onSubmit }: UseQuickPlanFormOptions) {
  const form = useForm<QuickPlanFormValues>({
    resolver: zodResolver(quickPlanSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      age: '',
      zipCode: '',
      maritalStatus: '',
    },
  });

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isValid: form.formState.isValid,
  };
}
